import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { getPIs, extractPI, createPI, receivePIItem, getPIFile } from '../../services/api';
import { toast } from 'sonner';
import { Upload, FileText, Sparkles, Package, CheckCircle, Loader2, Eye } from 'lucide-react';

const statusColor = (s) => ({
  pending: 'bg-gray-100 text-gray-700',
  partial: 'bg-yellow-100 text-yellow-700',
  complete: 'bg-green-100 text-green-700',
}[s] || 'bg-gray-100 text-gray-700');

export const PITab = ({ suppliers }) => {
  const [pis, setPIs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload/extract
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [supplierId, setSupplierId] = useState('');
  const [notes, setNotes] = useState('');
  const [extracting, setExtracting] = useState(false);

  // Confirm extracted items
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [draftItems, setDraftItems] = useState([]);
  const [draftFile, setDraftFile] = useState({ file_path: null, file_name: null });
  const [saving, setSaving] = useState(false);

  // Receive
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [activePI, setActivePI] = useState(null);
  const [receiveQtys, setReceiveQtys] = useState({});
  const [receivePrice, setReceivePrice] = useState({});
  const [receiveGst, setReceiveGst] = useState({});

  const fetchPIs = useCallback(async () => {
    try {
      const res = await getPIs();
      setPIs(res.data);
    } catch (e) {
      toast.error('Failed to load PIs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPIs(); }, [fetchPIs]);

  const handleExtract = async (e) => {
    e.preventDefault();
    if (!file || !supplierId) { toast.error('Select a supplier and a PI file'); return; }
    setExtracting(true);
    try {
      const res = await extractPI(file, supplierId);
      setDraftItems(res.data.line_items || []);
      setDraftFile({ file_path: res.data.file_path, file_name: res.data.file_name });
      setUploadOpen(false);
      setConfirmOpen(true);
      if ((res.data.line_items || []).length === 0) {
        toast.warning('No line items detected — you can add them manually');
      } else {
        toast.success(`Extracted ${res.data.line_items.length} line item(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  const updateDraft = (idx, field, value) => {
    setDraftItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };
  const addDraftRow = () => setDraftItems(prev => [...prev, { item_code: '', item_name: '', item_type: 'hardware', finish: '', material_type: '', length_m: 0, quantity: 0, unit: 'pcs', unit_price: 0 }]);
  const removeDraftRow = (idx) => setDraftItems(prev => prev.filter((_, i) => i !== idx));

  const handleSavePI = async () => {
    const clean = draftItems.filter(it => it.item_name && it.item_name.trim());
    if (clean.length === 0) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      await createPI({
        supplier_id: supplierId,
        file_path: draftFile.file_path,
        file_name: draftFile.file_name,
        notes,
        line_items: clean.map(it => ({
          item_code: String(it.item_code || ''),
          item_name: String(it.item_name),
          item_type: it.item_type === 'profile' ? 'profile' : 'hardware',
          finish: String(it.finish || ''),
          material_type: String(it.material_type || ''),
          length_m: parseFloat(it.length_m) || 0,
          quantity: parseFloat(it.quantity) || 0,
          unit: String(it.unit || 'pcs'),
          unit_price: parseFloat(it.unit_price) || 0,
        })),
      });
      toast.success('PI saved');
      setConfirmOpen(false);
      setFile(null); setSupplierId(''); setNotes(''); setDraftItems([]);
      fetchPIs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save PI');
    } finally {
      setSaving(false);
    }
  };

  const openReceive = (pi) => {
    setActivePI(pi);
    setReceiveQtys({}); setReceivePrice({}); setReceiveGst({});
    setReceiveOpen(true);
  };

  const viewPIFile = async (pi) => {
    try {
      const res = await getPIFile(pi.id);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Could not open the PI file');
    }
  };

  const handleReceive = async (idx) => {
    const qty = parseFloat(receiveQtys[idx]);
    if (!qty || qty <= 0) { toast.error('Enter a valid quantity'); return; }
    try {
      const price = (receivePrice[idx] !== undefined && receivePrice[idx] !== '') ? parseFloat(receivePrice[idx]) : undefined;
      const gst = parseFloat(receiveGst[idx]) || 0;
      const res = await receivePIItem(activePI.id, idx, qty, price, gst);
      setActivePI(res.data);
      setReceiveQtys(prev => ({ ...prev, [idx]: '' }));
      toast.success('Received — added to Material Library');
      fetchPIs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to receive');
    }
  };

  return (
    <div className="space-y-4" data-testid="pi-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Upload a supplier PI, auto-extract materials, then receive against it — received quantities flow into the Material Library.</p>
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button className="btn-accent" data-testid="upload-pi-btn"><Upload size={16} className="mr-1" />Upload PI</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Upload Supplier PI</DialogTitle></DialogHeader>
            <form onSubmit={handleExtract} className="space-y-4">
              <div>
                <Label>Supplier *</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger className="mt-1.5" data-testid="pi-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>PI File (PDF, image, Excel, CSV) *</Label>
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv"
                  onChange={(e) => setFile(e.target.files[0])} className="mt-1.5" data-testid="pi-file-input" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" placeholder="Optional" />
              </div>
              <Button type="submit" className="w-full btn-accent" disabled={extracting} data-testid="extract-pi-btn">
                {extracting ? <><Loader2 size={16} className="mr-2 animate-spin" />Extracting with AI...</> : <><Sparkles size={16} className="mr-2" />Extract Materials</>}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* PI list */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" /></div>
          ) : pis.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <FileText size={40} className="mb-3 opacity-50" /><p>No proforma invoices yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">PI #</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Items</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">GRN #</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pis.map(pi => (
                    <tr key={pi.id} className="hover:bg-muted/30" data-testid={`pi-row-${pi.id}`}>
                      <td className="px-4 py-3 font-mono text-sm">{pi.pi_number}</td>
                      <td className="px-4 py-3">{pi.supplier_name}</td>
                      <td className="px-4 py-3">{pi.line_items?.length || 0}</td>
                      <td className="px-4 py-3 font-mono text-sm">{pi.grn_number || '-'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor(pi.status)}`}>{pi.status}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {pi.file_path && (
                            <Button variant="ghost" size="sm" onClick={() => viewPIFile(pi)} data-testid={`view-pi-${pi.id}`}>
                              <Eye size={14} className="mr-1" />View PI
                            </Button>
                          )}
                          {pi.status !== 'complete' && (
                            <Button variant="outline" size="sm" onClick={() => openReceive(pi)} data-testid={`receive-pi-${pi.id}`}>
                              <Package size={14} className="mr-1" />Receive (GRN)
                            </Button>
                          )}
                          {pi.status === 'complete' && <span className="text-green-600 flex items-center gap-1 text-sm"><CheckCircle size={16} />Complete</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm extracted items */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader><DialogTitle>Confirm Extracted Materials</DialogTitle></DialogHeader>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left">Code</th>
                  <th className="px-2 py-2 text-left">Item Name</th>
                  <th className="px-2 py-2 text-left">Type</th>
                  <th className="px-2 py-2 text-left">Finish</th>
                  <th className="px-2 py-2 text-left">Material</th>
                  <th className="px-2 py-2 text-left">Len(m)</th>
                  <th className="px-2 py-2 text-left">Qty</th>
                  <th className="px-2 py-2 text-left">Unit</th>
                  <th className="px-2 py-2 text-left">Rate</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {draftItems.map((it, idx) => (
                  <tr key={idx} data-testid={`draft-row-${idx}`}>
                    <td className="px-1 py-1"><Input value={it.item_code || ''} onChange={(e) => updateDraft(idx, 'item_code', e.target.value)} className="h-8 w-24" /></td>
                    <td className="px-1 py-1"><Input value={it.item_name || ''} onChange={(e) => updateDraft(idx, 'item_name', e.target.value)} className="h-8" data-testid={`draft-name-${idx}`} /></td>
                    <td className="px-1 py-1"><select value={it.item_type || 'hardware'} onChange={(e) => updateDraft(idx, 'item_type', e.target.value)} className="h-8 border rounded text-xs px-1" data-testid={`draft-type-${idx}`}><option value="profile">Profile</option><option value="hardware">Hardware</option></select></td>
                    <td className="px-1 py-1 w-24"><Input value={it.finish || ''} onChange={(e) => updateDraft(idx, 'finish', e.target.value)} className="h-8" /></td>
                    <td className="px-1 py-1 w-24"><Input value={it.material_type || ''} onChange={(e) => updateDraft(idx, 'material_type', e.target.value)} className="h-8" /></td>
                    <td className="px-1 py-1 w-16"><Input type="number" value={it.length_m || 0} onChange={(e) => updateDraft(idx, 'length_m', e.target.value)} className="h-8" disabled={it.item_type !== 'profile'} /></td>
                    <td className="px-1 py-1 w-16"><Input type="number" value={it.quantity} onChange={(e) => updateDraft(idx, 'quantity', e.target.value)} className="h-8" /></td>
                    <td className="px-1 py-1 w-16"><Input value={it.unit || ''} onChange={(e) => updateDraft(idx, 'unit', e.target.value)} className="h-8" /></td>
                    <td className="px-1 py-1 w-20"><Input type="number" value={it.unit_price} onChange={(e) => updateDraft(idx, 'unit_price', e.target.value)} className="h-8" /></td>
                    <td className="px-1 py-1"><Button variant="ghost" size="sm" onClick={() => removeDraftRow(idx)}>✕</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center pt-2">
            <Button variant="outline" size="sm" onClick={addDraftRow} data-testid="add-draft-row-btn">+ Add Row</Button>
            <Button className="btn-accent" onClick={handleSavePI} disabled={saving} data-testid="save-pi-btn">
              {saving ? <><Loader2 size={16} className="mr-2 animate-spin" />Saving...</> : 'Save PI'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receive dialog */}
      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Receive Items — {activePI?.pi_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {activePI?.line_items?.map((item, idx) => {
              const pending = (parseFloat(item.quantity) || 0) - (parseFloat(item.received_quantity) || 0);
              return (
                <Card key={idx} className="p-3">
                  <div className="flex justify-between items-center gap-3">
                    <div>
                      <p className="font-medium">{item.item_name} <span className="text-xs text-muted-foreground">({item.item_type})</span></p>
                      <p className="text-xs text-muted-foreground">
                        {item.item_code ? `${item.item_code} · ` : ''}{item.item_type === 'profile' && item.length_m ? `${item.length_m}m · ` : ''}Ordered: {item.quantity} {item.unit} · Received: {item.received_quantity || 0}
                      </p>
                    </div>
                    {pending > 0 ? (
                      <div className="flex gap-2 items-center">
                        <Input type="number" placeholder={`Qty (${pending})`} value={receiveQtys[idx] || ''}
                          onChange={(e) => setReceiveQtys(prev => ({ ...prev, [idx]: e.target.value }))}
                          className="h-8 w-20" data-testid={`pi-receive-qty-${idx}`} />
                        <Input type="number" placeholder="Price/UOM" value={receivePrice[idx] || ''}
                          onChange={(e) => setReceivePrice(prev => ({ ...prev, [idx]: e.target.value }))}
                          className="h-8 w-24" data-testid={`pi-receive-price-${idx}`} />
                        <Input type="number" placeholder="GST%" value={receiveGst[idx] || ''}
                          onChange={(e) => setReceiveGst(prev => ({ ...prev, [idx]: e.target.value }))}
                          className="h-8 w-16" data-testid={`pi-receive-gst-${idx}`} />
                        <Button size="sm" variant="outline" onClick={() => handleReceive(idx)} data-testid={`pi-receive-confirm-${idx}`}>Receive</Button>
                      </div>
                    ) : <CheckCircle size={20} className="text-green-600" />}
                  </div>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PITab;
